import React from "react";
import {
  Box,
  Tooltip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  useSettingsUI,
  useSolutionType,
  useGraphicsConfigs,
  useListViewConfigs,
  useGeneralSettingsJson,
  useSystemId,
  useUIState,
} from "../../hooks/useReduxSelectors";
import { toggleDeviceTags, toggleLinearGauges } from "../../ducks/settingsUIDuck";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ImageIcon from "@mui/icons-material/Image";
import ScienceIcon from "@mui/icons-material/Science";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TimelineIcon from "@mui/icons-material/Timeline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import SellIcon from "@mui/icons-material/Sell";
import TuneIcon from "@mui/icons-material/Tune";

const NAV_WIDTH = 64;

const MENU_ITEMS = [
  { key: "overview", label: "Overview", icon: DashboardIcon },
  { key: "list-view", label: "List-View", icon: TableRowsIcon },
  { key: "graphical-view", label: "Graphical-view", icon: ImageIcon },
  { key: "sample-view", label: "Sample-view", icon: ScienceIcon },
  { key: "alarms", label: "Alarms", icon: NotificationsIcon },
  { key: "trend", label: "Trend", icon: TimelineIcon },
  { key: "report", label: "Report", icon: AssessmentIcon },
];

const BOTTOM_ITEMS = [
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

const navItemSx = (isSelected, theme) => ({
  width: NAV_WIDTH,
  height: NAV_WIDTH,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderTop: "1px solid",
  borderBottom: "1px solid",
  borderColor: isSelected ? theme.palette.surface.mutedBorder : "divider",
  bgcolor: isSelected ? "action.selected" : "transparent",
  color: isSelected ? "secondary.main" : "text.primary",
  "&:hover": { bgcolor: "action.hover" },
});

const LeftSideNav = ({ onSelect, selectedMenu, variant }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const systemId = useSystemId();
  const settingsUI = useSettingsUI();
  const showLinearGauges = settingsUI?.showLinearGauges;
  const showTags = settingsUI?.showDeviceTags;
  const solutionType = useSolutionType();
  const hasGraphicsConfigs = useGraphicsConfigs()?.show || false;
  const hasListView = useListViewConfigs()?.show || false;
  const solution = useGeneralSettingsJson()?.solution || "MeasurementSystem";
  const ui = useUIState();
  const theme = useTheme();

  const serviceModeActive = ui?.serviceMode?.isActive;

  const visibleMainItems = MENU_ITEMS.filter((item) => {
    if (item.key === "sample-view" && solution !== "SWAS") return false;
    if (item.key === "graphical-view" && !hasGraphicsConfigs) return false;
    if (item.key === "list-view" && !hasListView) return false;
    return true;
  });

  const visibleBottomItems = BOTTOM_ITEMS.filter((item) => {
    if (solutionType === "lora" && item.key === "settings") return false;
    return true;
  });

  // Expanded variant for mobile drawer
  if (variant === "expanded") {
    return (
      <Box sx={{ width: 240, height: "100%", display: "flex", flexDirection: "column" }}>
        <List sx={{ flex: 1, pt: 0 }}>
          {visibleMainItems.map((item) => {
            const Icon = item.icon;
            const isSelected = item.key === selectedMenu;
            return (
              <ListItemButton
                key={item.key}
                selected={isSelected}
                onClick={() => onSelect(item.key)}
                sx={{
                  "&.Mui-selected": {
                    bgcolor: "action.selected",
                    borderLeft: "3px solid",
                    borderColor: "secondary.main",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon sx={{ color: isSelected ? "secondary.main" : "text.primary" }} />
                </ListItemIcon>
                <ListItemText primary={t(item.label)} />
              </ListItemButton>
            );
          })}
        </List>
        <Divider />
        <List>
          <ListItemButton
            selected={showTags}
            onClick={() => dispatch(toggleDeviceTags({ systemId }))}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SellIcon sx={{ color: showTags ? "secondary.main" : "text.primary" }} />
            </ListItemIcon>
            <ListItemText primary={showTags ? t("Tags ON") : t("Tags OFF")} />
          </ListItemButton>
          <ListItemButton
            selected={showLinearGauges}
            onClick={() => dispatch(toggleLinearGauges({ systemId }))}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <TuneIcon sx={{ color: showLinearGauges ? "secondary.main" : "text.primary" }} />
            </ListItemIcon>
            <ListItemText primary={showLinearGauges ? t("Gauges ON") : t("Gauges OFF")} />
          </ListItemButton>
          {visibleBottomItems.map((item) => {
            const Icon = item.icon;
            const isSelected = item.key === selectedMenu;
            return (
              <ListItemButton
                key={item.key}
                selected={isSelected}
                onClick={() => onSelect(item.key)}
                sx={{
                  "&.Mui-selected": {
                    bgcolor: "action.selected",
                    borderRight: "3px solid",
                    borderColor: "secondary.main",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon sx={{ color: isSelected ? "secondary.main" : "text.primary" }} />
                </ListItemIcon>
                <ListItemText primary={t(item.label)} />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    );
  }

  // Desktop rail variant
  const renderNavItem = (item, index) => {
    const Icon = item.icon;
    const isSelected = item.key === selectedMenu;
    return (
      <Tooltip key={item.key} title={t(item.label)} placement="right">
        <Box sx={{ ...navItemSx(isSelected, theme), ...(index === 0 && { borderTop: "none" }) }} onClick={() => onSelect(item.key)}>
          <Icon fontSize="large" sx={{ color: isSelected ? "secondary.main" : "text.primary" }} />
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      component="nav"
      sx={{
        width: NAV_WIDTH,
        minWidth: NAV_WIDTH,
        height: "100%",
        bgcolor: "inherit",
        display: "flex",
        flexDirection: "column",
        zIndex: 10,
        boxShadow: 2,
        borderRight: '2px solid',
        borderColor: theme.palette.surface.mutedBorder,
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {visibleMainItems.map(renderNavItem)}
      </Box>

      <Box sx={{ mt: "auto" }}>
        <Tooltip title={showTags ? "Tags ON" : "Tags OFF"} placement="right">
          <Box
            sx={{
              ...navItemSx(showTags, theme),
              borderTop: showTags ? "2px solid" : "1px solid",
              borderTopColor: showTags ? theme.palette.surface.mutedBorder : "divider",
            }}
            onClick={() => dispatch(toggleDeviceTags({ systemId }))}
          >
            <SellIcon fontSize="large" sx={{ color: showTags ? "secondary.main" : "text.primary" }} />
          </Box>
        </Tooltip>

        <Tooltip title={showLinearGauges ? "Linear Gauges ON" : "Linear Gauges OFF"} placement="right">
          <Box
            sx={{
              ...navItemSx(showLinearGauges, theme),
              borderTop: showLinearGauges ? "2px solid" : "1px solid",
              borderTopColor: showLinearGauges ? theme.palette.surface.mutedBorder : "divider",
            }}
            onClick={() => dispatch(toggleLinearGauges({ systemId }))}
          >
            <TuneIcon fontSize="large" sx={{ color: showLinearGauges ? "secondary.main" : "text.primary" }} />
          </Box>
        </Tooltip>

        {visibleBottomItems.map(renderNavItem)}
      </Box>
    </Box>
  );
};

export default LeftSideNav;
